// client/src/components/pages/OperationOverview/RoutePerformance.tsx
// (converted from RoutePerformance.js, ST-00218 P3 — CLAUDE.md TypeScript rule)
//
// The Route Performance tab: weighed loads and tonnes per route on both sides, the last data
// date and the variance. ST-00218 P3: the variance is the route's own matched figure from the
// server (routePerformanceFigures.ts) — "n/a" without (full) receipt data, sorted last — and
// a note when a "Truck + time" route shows delivery-number figures.
import { useState, useEffect, useMemo, type ComponentType } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../../services/api';
import LexiBridgeLoaderJs from '../../common/LexiBridgeLoader';
import MatchingNote from './MatchingNote';
import { fallbackNoteOf } from './overviewKpis';
import { performanceTotals, rowVariance, rowVarianceNaReason } from './routePerformanceFigures';
import type { RoutePerformanceRow } from '../../../types/clientPages/operationOverview';

// Typed view of the JS component.
const LexiBridgeLoader = LexiBridgeLoaderJs as unknown as ComponentType<{
  message?: string;
  compact?: boolean;
}>;

type SortKey =
  | 'name'
  | 'dispatchWeight'
  | 'receiptWeight'
  | 'dispatchCount'
  | 'receiptCount'
  | 'lastTicketDate'
  | 'variance';
interface SortConfig {
  key: SortKey | null;
  direction: 'asc' | 'desc';
}

const DISP = '#e8943a';
const RECV = '#45B7AA';

// Column tints are alpha washes so they composite correctly over the light and
// dark surface tokens instead of baking in a light-mode-only hex.
const DISP_RGB = '232 148 58';
const dispTint = (a: number): string => `rgb(${DISP_RGB} / ${a})`;
const recvTint = (a: number): string => `rgb(var(--accent-rgb) / ${a})`;

const fmt = (v: number | null | undefined): string =>
  v == null || isNaN(v) ? '—' : Number(v).toLocaleString('en-ZA', { maximumFractionDigits: 0 });
const toISO = (d: Date | string | null | undefined): string => {
  if (!d) return '';
  const dt = d instanceof Date ? d : new Date(d);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
};

// null = n/a (no receipt data): a neutral colour.
const vClr = (p: number | null): string =>
  p === null
    ? 'var(--text-muted)'
    : Math.abs(p) > 5
      ? 'var(--danger)'
      : Math.abs(p) > 3
        ? 'var(--warning)'
        : 'var(--success)';
const vBg = (p: number | null): string =>
  p === null
    ? 'bg-slate-100 text-slate-700 border-slate-300'
    : Math.abs(p) > 5
      ? 'bg-red-50 text-red-700 border-red-200'
      : Math.abs(p) > 3
        ? 'bg-amber-50 text-amber-700 border-amber-200'
        : 'bg-emerald-50 text-emerald-700 border-emerald-200';
const signedPct = (p: number | null, digits: number): string =>
  p === null ? 'n/a' : `${p >= 0 ? '+' : ''}${p.toFixed(digits)}%`;

const relDate = (dateStr: string | Date | null | undefined) => {
  if (!dateStr) return { text: '—', sub: '', stale: true };
  const d = new Date(dateStr);
  const days = Math.floor((new Date().getTime() - d.getTime()) / 86400000);
  const text = d.toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' });
  if (days === 0) return { text, sub: 'today', stale: false };
  if (days === 1) return { text, sub: 'yesterday', stale: false };
  if (days <= 3) return { text, sub: `${days}d ago`, stale: false };
  return { text, sub: `${days}d ago`, stale: true };
};

export interface RoutePerformanceProps {
  startDate: Date;
  endDate: Date;
}

const RoutePerformance = ({ startDate, endDate }: RoutePerformanceProps) => {
  const navigate = useNavigate();
  const [data, setData] = useState<RoutePerformanceRow[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    key: 'dispatchWeight',
    direction: 'desc',
  });

  useEffect(() => {
    if (!startDate || !endDate) return;
    let dead = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const r = await api.routeSummary.getRoutePerformance(toISO(startDate), toISO(endDate));
        if (!dead) setData((r.data as RoutePerformanceRow[] | null) || []);
      } catch {
        if (!dead) setError('Failed to load route performance data');
      } finally {
        if (!dead) setLoading(false);
      }
    })();
    return () => {
      dead = true;
    };
  }, [startDate, endDate]);

  const filtered = useMemo(() => {
    if (!data) return [];
    return data.filter(
      (r) =>
        (r.dispatchWeight > 0 || r.receiptWeight > 0) &&
        r.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [data, searchTerm]);

  const sorted = useMemo(() => {
    if (!sortConfig.key) return filtered;
    return [...filtered].sort((a, b) => {
      let aV: string | number;
      let bV: string | number;
      switch (sortConfig.key) {
        case 'name':
          aV = a.name.toLowerCase();
          bV = b.name.toLowerCase();
          break;
        case 'dispatchWeight':
          aV = a.dispatchWeight;
          bV = b.dispatchWeight;
          break;
        case 'receiptWeight':
          aV = a.receiptWeight;
          bV = b.receiptWeight;
          break;
        case 'dispatchCount':
          aV = a.dispatchCount;
          bV = b.dispatchCount;
          break;
        case 'receiptCount':
          aV = a.receiptCount;
          bV = b.receiptCount;
          break;
        case 'lastTicketDate':
          aV = a.lastTicketDate ? new Date(a.lastTicketDate).getTime() : 0;
          bV = b.lastTicketDate ? new Date(b.lastTicketDate).getTime() : 0;
          break;
        case 'variance': {
          // The variance shown (ST-00218 P3); n/a goes last whichever way the column sorts.
          const av = rowVariance(a);
          const bv = rowVariance(b);
          if (av === null || bv === null) return av === bv ? 0 : av === null ? 1 : -1;
          aV = av;
          bV = bv;
          break;
        }
        default:
          return 0;
      }
      if (aV < bV) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aV > bV) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filtered, sortConfig]);

  // Weighed sums for the columns; the variance is Σ received against Σ adjusted dispatch.
  const totals = useMemo(() => performanceTotals(filtered), [filtered]);
  const matchingNote = useMemo(() => fallbackNoteOf(filtered), [filtered]);

  const handleSort = (key: SortKey) =>
    setSortConfig((p) => ({
      key,
      direction: p.key === key && p.direction === 'desc' ? 'asc' : 'desc',
    }));

  const Arrow = ({ col }: { col: SortKey }) => {
    if (sortConfig.key !== col) return <span className="opacity-30 ml-1">&#x21C5;</span>;
    return (
      <span className="ml-1 text-primary-500">{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
    );
  };

  const goTo = (route: RoutePerformanceRow) => {
    const p = new URLSearchParams({
      groupingId: route.id.toString(),
      startDate: toISO(startDate),
      endDate: toISO(endDate),
    });
    navigate(`/route-summary?${p.toString()}`, {
      state: { fromOperationsOverview: true, groupingName: route.name },
    });
  };

  if (loading)
    return (
      <div className="flex items-center justify-center py-32 bg-slate-50">
        <LexiBridgeLoader message="Loading route performance" />
      </div>
    );
  if (error)
    return (
      <div className="max-w-[1800px] mx-auto px-6 py-8 bg-slate-50">
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <p className="text-red-600 font-medium">{error}</p>
        </div>
      </div>
    );

  const maxW = sorted.length > 0 ? Math.max(...sorted.map((r) => r.dispatchWeight)) : 1;
  const diffTotal = totals ? totals.diff : 0;
  const varTotal = totals ? totals.variance : null;

  return (
    <div className="bg-slate-50 min-h-[400px]">
      <div className="max-w-[1800px] mx-auto px-3 sm:px-6 lg:px-8 py-2 sm:py-3 space-y-3">
        <MatchingNote note={matchingNote} />
        {/* KPI Strip */}
        {totals && (
          <div className="bg-surface rounded-xl border border-slate-200 shadow-sm px-5 py-4">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-1.5 h-1.5 rounded-full bg-primary-500 animate-pulse" />
              <span className="text-xs font-display font-semibold text-slate-400 uppercase tracking-widest">
                {filtered.length} route{filtered.length !== 1 ? 's' : ''}
              </span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                {
                  label: 'Dispatched',
                  val: fmt(totals.dW),
                  sub: `${fmt(totals.dC)} loads`,
                  accent: DISP,
                },
                {
                  label: 'Received',
                  val: fmt(totals.rW),
                  sub: `${fmt(totals.rC)} loads`,
                  accent: RECV,
                },
                {
                  label: 'Net Variance',
                  val: `${diffTotal >= 0 ? '+' : ''}${fmt(diffTotal)}`,
                  sub: varTotal !== null ? `${varTotal.toFixed(2)}% after BF/CF` : '—',
                  accent: vClr(varTotal ?? 0),
                },
                {
                  label: 'Latest Data',
                  val: totals.latestDate
                    ? totals.latestDate.toLocaleDateString('en-ZA', {
                        day: '2-digit',
                        month: 'short',
                      })
                    : '—',
                  sub: totals.latestDate ? relDate(totals.latestDate).sub : '',
                  accent: '#6366f1',
                },
              ].map((k, i) => (
                <div
                  key={i}
                  className="rounded-lg border border-slate-100 px-3.5 py-3 relative overflow-hidden bg-slate-50 hover:border-slate-200 transition-colors"
                >
                  <div
                    className="absolute top-0 left-0 w-[3px] h-full rounded-r"
                    style={{ background: k.accent }}
                  />
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1 pl-2">
                    {k.label}
                  </p>
                  <p className="font-display text-xl font-bold text-slate-800 tabular-nums tracking-tight pl-2 leading-none">
                    {k.val} <span className="text-xs font-normal text-slate-400">MT</span>
                  </p>
                  <p
                    className="text-xs font-mono tabular-nums pl-2 mt-0.5"
                    style={{ color: k.accent }}
                  >
                    {k.sub}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Search */}
        <div className="flex items-center gap-3">
          <div className="relative flex-1 max-w-sm">
            <svg
              className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            <input
              type="text"
              placeholder="Filter routes…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-4 py-2 border border-slate-200 rounded-lg text-sm bg-surface focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-400 transition-colors"
            />
          </div>
          <span className="text-xs text-slate-400 font-mono tabular-nums">
            {sorted.length} routes
          </span>
        </div>

        {/* Table */}
        {sorted.length === 0 ? (
          <div className="bg-surface rounded-xl border border-slate-200 shadow-sm p-12 text-center">
            <p className="text-slate-400 text-sm">No routes with data in this period</p>
          </div>
        ) : (
          <div className="bg-surface rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="overflow-x-auto" style={{ scrollbarWidth: 'thin' }}>
              <table className="w-full min-w-[580px]">
                <colgroup>
                  <col />
                  <col />
                  <col />
                  <col />
                  <col />
                  <col />
                  <col />
                </colgroup>

                <thead>
                  <tr>
                    <th className="bg-slate-100 border-b border-slate-300" />
                    <th
                      colSpan={2}
                      className="py-2 text-center text-xs font-bold uppercase tracking-widest border-b border-slate-300"
                      style={{ color: DISP, background: dispTint(0.22) }}
                    >
                      Dispatched
                    </th>
                    <th
                      colSpan={2}
                      className="py-2 text-center text-xs font-bold uppercase tracking-widest border-b border-slate-300"
                      style={{ color: 'var(--accent-text)', background: recvTint(0.22) }}
                    >
                      Received
                    </th>
                    <th className="bg-slate-100 border-b border-slate-300" />
                    <th className="bg-slate-100 border-b border-slate-300" />
                  </tr>

                  <tr className="border-b-2 border-slate-300">
                    <th
                      className="pl-4 pr-3 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500 bg-slate-100 cursor-pointer hover:bg-slate-200/70"
                      onClick={() => handleSort('name')}
                    >
                      Route <Arrow col="name" />
                    </th>
                    <th
                      className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider cursor-pointer hover:brightness-95 hidden sm:table-cell border-l-2"
                      style={{
                        color: 'var(--warning-text)',
                        borderLeftColor: DISP,
                        background: dispTint(0.22),
                      }}
                      onClick={() => handleSort('dispatchCount')}
                    >
                      Loads <Arrow col="dispatchCount" />
                    </th>
                    <th
                      className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider cursor-pointer hover:brightness-95"
                      style={{ color: 'var(--warning-text)', background: dispTint(0.22) }}
                      onClick={() => handleSort('dispatchWeight')}
                    >
                      Tonnage <Arrow col="dispatchWeight" />
                    </th>
                    <th
                      className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider cursor-pointer hover:brightness-95 border-l-2"
                      style={{
                        color: 'var(--accent-text)',
                        borderLeftColor: 'var(--border-strong)',
                        background: recvTint(0.22),
                      }}
                      onClick={() => handleSort('receiptWeight')}
                    >
                      Tonnage <Arrow col="receiptWeight" />
                    </th>
                    <th
                      className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider cursor-pointer hover:brightness-95 hidden sm:table-cell"
                      style={{ color: 'var(--accent-text)', background: recvTint(0.22) }}
                      onClick={() => handleSort('receiptCount')}
                    >
                      Loads <Arrow col="receiptCount" />
                    </th>
                    <th
                      className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wider text-slate-500 bg-slate-100 cursor-pointer hover:bg-slate-200/70 hidden md:table-cell"
                      onClick={() => handleSort('lastTicketDate')}
                    >
                      Last Data <Arrow col="lastTicketDate" />
                    </th>
                    <th
                      className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wider text-slate-500 bg-slate-100 cursor-pointer hover:bg-slate-200/70"
                      onClick={() => handleSort('variance')}
                      title="Received against dispatched after Brought / Carried Forward, as on Route Detail"
                    >
                      Variance <Arrow col="variance" />
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {sorted.map((route, idx) => {
                    const v = rowVariance(route);
                    const dateInfo = relDate(route.lastTicketDate);
                    const barW = maxW > 0 ? Math.max((route.dispatchWeight / maxW) * 100, 3) : 0;
                    const odd = idx % 2 === 1;

                    return (
                      <tr
                        key={route.id}
                        className={`border-b border-slate-200 cursor-pointer transition-all duration-150 hover:shadow-[inset_0_0_0_1px_rgb(var(--accent-rgb)/0.25)] ${odd ? '' : ''}`}
                        style={odd ? { background: 'var(--surface-hover)' } : undefined}
                        onClick={() => goTo(route)}
                      >
                        <td
                          className="pl-4 pr-3 py-3"
                          style={
                            odd
                              ? { background: 'var(--surface-hover)' }
                              : { background: 'var(--surface)' }
                          }
                        >
                          <div className="flex items-center gap-3">
                            <div
                              className="w-1 self-stretch rounded-full flex-shrink-0"
                              style={{ background: vClr(v), minHeight: 32 }}
                            />
                            <div className="min-w-0">
                              <span className="text-sm font-display font-bold text-slate-800 truncate block">
                                {route.name}
                              </span>
                              <div className="flex items-center gap-1.5 mt-1">
                                <div className="w-20 h-1 bg-slate-200 rounded-full overflow-hidden">
                                  <div
                                    className="h-full rounded-full"
                                    style={{ width: `${barW}%`, background: DISP, opacity: 0.55 }}
                                  />
                                </div>
                                <span
                                  className="text-xs font-mono font-semibold tabular-nums"
                                  style={{ color: vClr(v) }}
                                >
                                  {signedPct(v, 1)}
                                </span>
                              </div>
                            </div>
                          </div>
                        </td>

                        <td
                          className="px-4 py-3 text-right hidden sm:table-cell border-l"
                          style={{
                            borderLeftColor: `${DISP}30`,
                            background: odd ? dispTint(0.09) : dispTint(0.06),
                          }}
                        >
                          <span className="font-mono text-sm text-slate-600 tabular-nums">
                            {fmt(route.dispatchCount)}
                          </span>
                        </td>
                        <td
                          className="px-4 py-3 text-right"
                          style={{ background: odd ? dispTint(0.09) : dispTint(0.06) }}
                        >
                          <span className="font-mono text-sm font-bold text-slate-800 tabular-nums">
                            {fmt(route.dispatchWeight)}
                          </span>
                        </td>

                        <td
                          className="px-4 py-3 text-right border-l-2 border-slate-300"
                          style={{ background: odd ? recvTint(0.12) : recvTint(0.07) }}
                        >
                          <span className="font-mono text-sm font-bold text-slate-800 tabular-nums">
                            {fmt(route.receiptWeight)}
                          </span>
                        </td>
                        <td
                          className="px-4 py-3 text-right hidden sm:table-cell"
                          style={{ background: odd ? recvTint(0.12) : recvTint(0.07) }}
                        >
                          <span className="font-mono text-sm text-slate-600 tabular-nums">
                            {fmt(route.receiptCount)}
                          </span>
                        </td>

                        <td
                          className="px-4 py-3 text-right hidden md:table-cell"
                          style={
                            odd
                              ? { background: 'var(--surface-hover)' }
                              : { background: 'var(--surface)' }
                          }
                        >
                          <span className="font-mono text-sm text-slate-600 tabular-nums">
                            {dateInfo.text}
                          </span>
                          <span
                            className={`block text-xs font-mono tabular-nums ${dateInfo.stale ? 'text-amber-500' : 'text-emerald-600'}`}
                          >
                            {dateInfo.sub}
                          </span>
                        </td>

                        <td
                          className="px-4 py-3 text-right"
                          style={
                            odd
                              ? { background: 'var(--surface-hover)' }
                              : { background: 'var(--surface)' }
                          }
                        >
                          <span
                            className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-bold font-mono tabular-nums border ${vBg(v)}`}
                            title={
                              v === null
                                ? `Variance not available: ${rowVarianceNaReason(route)}`
                                : undefined
                            }
                          >
                            {signedPct(v, 2)}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>

                {totals && (
                  <tfoot>
                    <tr className="border-t-2 border-slate-400">
                      <td className="pl-4 pr-3 py-3 bg-slate-200/60">
                        <span className="text-xs font-display font-black text-slate-700 uppercase tracking-widest">
                          Totals
                        </span>
                      </td>
                      <td
                        className="px-4 py-3 text-right hidden sm:table-cell border-l-2"
                        style={{ borderLeftColor: DISP, background: dispTint(0.22) }}
                      >
                        <span
                          className="font-mono text-sm font-bold tabular-nums"
                          style={{ color: 'var(--warning-text)' }}
                        >
                          {fmt(totals.dC)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right" style={{ background: dispTint(0.22) }}>
                        <span
                          className="font-mono text-sm font-black tabular-nums"
                          style={{ color: 'var(--warning-text)' }}
                        >
                          {fmt(totals.dW)}
                        </span>
                      </td>
                      <td
                        className="px-4 py-3 text-right border-l-2 border-slate-400"
                        style={{ background: recvTint(0.22) }}
                      >
                        <span
                          className="font-mono text-sm font-black tabular-nums"
                          style={{ color: 'var(--accent-text)' }}
                        >
                          {fmt(totals.rW)}
                        </span>
                      </td>
                      <td
                        className="px-4 py-3 text-right hidden sm:table-cell"
                        style={{ background: recvTint(0.22) }}
                      >
                        <span
                          className="font-mono text-sm font-bold tabular-nums"
                          style={{ color: 'var(--accent-text)' }}
                        >
                          {fmt(totals.rC)}
                        </span>
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell bg-slate-200/60" />
                      <td className="px-4 py-3 text-right bg-slate-200/60">
                        {varTotal !== null && (
                          <span
                            className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-black font-mono tabular-nums border ${vBg(varTotal)}`}
                          >
                            {signedPct(varTotal, 2)}
                          </span>
                        )}
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RoutePerformance;
